import { useEffect, useId, useRef, useState } from "react";
import type { LearnerReport } from "../types";

/** Scores for the supplied recent sessions, in chronological order. */
export function ProgressChart({ dialogs }: { dialogs: LearnerReport["dialogs"] }) {
  const host = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(700);
  const titleId = useId();
  useEffect(() => {
    if (!host.current) return;
    const observer = new ResizeObserver(([entry]) => {
      setWidth(Math.max(280, entry.contentRect.width));
    });
    observer.observe(host.current);
    return () => observer.disconnect();
  }, []);

  const height = 200, left = 42, right = 18, top = 18, bottom = 42;
  const x = (i: number) => left + i / Math.max(1, dialogs.length - 1) * (width - left - right);
  const y = (score: number) => height - bottom - score / 100 * (height - top - bottom);
  const date = (value: string) => new Date(value).toLocaleDateString("en-ZA", {
    day: "numeric", month: "short", year: "numeric",
  });
  const path = dialogs.map((d, i) => `${i ? "L" : "M"}${x(i)} ${y(d.score)}`).join(" ");

  return (
    <div ref={host} className="progress-chart">
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" role="img" aria-labelledby={titleId}>
        <title id={titleId}>Recent dialogue scores, oldest to newest, on a scale from 0 to 100 percent</title>
        {[0, 50, 100].map((score) => (
          <g key={score}>
            <line x1={left} y1={y(score)} x2={width - right} y2={y(score)} className="progress-chart-grid" />
            <text x={left - 8} y={y(score) + 4} textAnchor="end">{score}%</text>
          </g>
        ))}
        <line x1={left} y1={y(70)} x2={width - right} y2={y(70)} className="progress-chart-reference" />
        <path d={path} fill="none" stroke="var(--accent)" strokeWidth="2.5" />
        {dialogs.map((d, i) => (
          <circle key={d.session_id} cx={x(i)} cy={y(d.score)} r="4" fill="var(--accent)">
            <title>{date(d.completed_at)}: {d.score}% — {d.lesson_title || "Lesson unavailable"}</title>
          </circle>
        ))}
        {dialogs.length > 0 && <>
          <text x={left} y={height - 17}>{date(dialogs[0].completed_at)}</text>
          <text x={width - right} y={height - 17} textAnchor="end">{date(dialogs[dialogs.length - 1].completed_at)}</text>
        </>}
      </svg>
      <details>
        <summary>View dates and scores</summary>
        <ol className="progress-chart-values">
          {dialogs.map((d) => (
            <li key={d.session_id}>{date(d.completed_at)} · {d.lesson_title || "Lesson unavailable"} · <strong>{d.score}%</strong></li>
          ))}
        </ol>
      </details>
    </div>
  );
}
